import {useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {mediaGalleryImageT} from '@/lib/types/media.types';
import {productT} from '@/lib/types/product.types';

import {RootState} from '@/lib/redux/store';

import {MediaInstance} from '@/lib/utils';

import {ImageGalleryComponent} from '@/components/imageGalleryComponent';
import {ImageUploadModalComponent} from '@/components/imageUploadModalComponent';
import {StatsRowComponent} from '@/components/statsRowComponent';

export const ProductDetailsMediaGalleryComponent = ({product, productId}: {product: productT; productId: number}) => {
  const {isAdmin} = useSelector((state: RootState) => state.account);
  const [images, setImages] = useState<mediaGalleryImageT[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);

  const handleRefresh = useCallback(async () => {
    setIsLoading(true);
    const productImages = await MediaInstance.getImages('Product_Media', productId, 'product_id');

    if (!productImages || productImages === undefined) return;

    setImages(productImages);
    setIsLoading(false);
  }, [productId, setIsLoading, setImages]);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  const getGalleryStats = (images: mediaGalleryImageT[]) => {
    const totalImages = images.length;
    const activeImages = images.filter(image => !image.date_removed).length;
    const inactiveImages = totalImages - activeImages;

    return {
      totalImages,
      activeImages,
      inactiveImages,
    };
  };

  const {totalImages} = getGalleryStats(images);

  const imageStats = [
    {
      title: 'Total Images',
      value: totalImages,
      subtitle: 'This Month',
      change: 1.03,
      icon: 'ri-price-tag-3-line',
      color: 'primary',
      cardClass: 'Brands-main-card primary',
    },
  ];

  const handleImageUpload = async (files: File[]) => {
    const formData = new FormData();

    files.forEach(file => {
      formData.append('file', file);
    });

    // product name with no spaces
    const path = product?.product_name?.replace(/\s/g, '');

    const res = await MediaInstance.uploadImages({
      bucket: 'media',
      path: `products/${path}`,
      table: 'Product_Media',
      id: productId,
      key: 'product_id',
      formData,
    });

    if (res) {
      setModalVisible(false);
      setImages([...images, ...res]);
    }
  };

  return (
    <>
      <div className="col-span-12 md:col-span-12 lg:col-span-8 xl:col-span-8 xxl:col-span-9">
        <div className="flex items-start justify-between">
          <StatsRowComponent stats={imageStats} />
          <div className="col-span-12 mt-0 flex flex-col gap-2 text-center">
            {isAdmin && (
              <button
                onClick={() => setModalVisible(true)}
                type="button"
                className="ti-btn ti-btn-primary !font-medium"
              >
                Add New Image
              </button>
            )}
            <button onClick={handleRefresh} type="button" className="ti-btn ti-btn-success w-full !font-medium">
              Refresh
            </button>
          </div>
        </div>

        {images && <ImageGalleryComponent images={images} isLoading={isLoading} />}
      </div>

      <ImageUploadModalComponent
        isVisible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSubmit={files => handleImageUpload(files)}
        title="Upload Product Images"
        maxFiles={5}
        allowMultiple={true}
      />
    </>
  );
};
